interface IconProps {
  className?: string;
}

export function CalculatorIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <rect x="5" y="2" width="14" height="20" rx="3" />
      <path d="M8 6h8M8 11h1m6 0h1M8 15h1m6 0h1M8 19h1m6 0h1" />
    </svg>
  );
}

export function BackspaceIcon({ className = 'h-5 w-5' }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <path d="M9 5h12v14H9l-7-7 7-7zM12 9l5 6m0-6-5 6" />
    </svg>
  );
}

export function PlayIcon({ className = 'h-4 w-4' }: IconProps) {
  return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
    <path d="M7 3v18l15-9z" />
  </svg>;
}

/** A counter-clockwise arrow: the near-universal sign for "undo". */
export function UndoIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M3 8h11a6 6 0 0 1 0 12H8" />
      <path d="M3 8l5-5M3 8l5 5" />
    </svg>
  );
}

/** A crown, for ending a frame — the point at which somebody has won one. */
export function CrownIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M3 8.5l3.8 3L12 4l5.2 7.5 3.8-3-1.9 10H4.9L3 8.5z" />
      <rect x="4.9" y="19" width="14.2" height="2.2" rx="0.6" />
    </svg>
  );
}

/**
 * Two arrows swapping places: the table changing hands.
 *
 * Switch player is the most-pressed button in the room, and a symbol is found
 * faster than a word by somebody who is looking at the table rather than the
 * screen.
 */
export function SwitchIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M4 8h13M13 4l4 4-4 4" />
      <path d="M20 16H7m4 4-4-4 4-4" />
    </svg>
  );
}

/** A tick, for the things a mode actually does. */
export function CheckIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M4 12.5l5.5 5.5L20 6" />
    </svg>
  );
}

/**
 * A bang in a circle: the sign that a stroke has gone wrong.
 *
 * It was a warning triangle — the road-sign shape, all hard corners and a
 * point at the top, which is the loudest glyph in a set and was sitting on the
 * quietest surface. Everything else on this board is a circle or a capsule,
 * and the system icon for "something is wrong here" is a bang in a ring. It
 * says the same thing without shouting, and it sits in a pill without a corner
 * poking out of the optical centre.
 */
export function FoulIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.4v6M12 16.5v.1" />
    </svg>
  );
}

/** A trophy, for the leaderboards. */
export function TrophyIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M7 6H4v1.5A3.5 3.5 0 0 0 7.5 11" />
      <path d="M17 6h3v1.5a3.5 3.5 0 0 1-3.5 3.5" />
      <path d="M12 14v3.5" />
      <path d="M8.5 21h7l-.8-3.5H9.3L8.5 21z" />
    </svg>
  );
}
